import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { ChargeBasis, ChargeBasisStats } from '../../models/forwarding/charge-basis.model';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ChargeBasisService {
  private apiUrl = `${environment.apiUrl}/forwarding/charge-basis`;
  private chargesSubject = new BehaviorSubject<ChargeBasis[]>([]);
  public charges$ = this.chargesSubject.asObservable();

  constructor(private http: HttpClient) {
    this.loadCharges();
  }

  private loadCharges(): void {
    console.log('[ChargeBasisService] Loading charges from:', this.apiUrl);
    this.http.get<ChargeBasis[]>(this.apiUrl).subscribe({
      next: (charges) => {
        console.log('[ChargeBasisService] Successfully loaded charges from API:', charges.length, charges);
        this.chargesSubject.next(charges);
      },
      error: (error) => {
        console.error('[ChargeBasisService] Error loading charge basis from API, falling back to mock data:', error);
        const mockCharges = this.getMockCharges();
        console.log('[ChargeBasisService] Using mock data:', mockCharges.length, 'items');
        this.chargesSubject.next(mockCharges);
      }
    });
  }

  getCharges(): Observable<ChargeBasis[]> {
    return this.charges$;
  }

  getStats(): Observable<ChargeBasisStats> {
    return this.http.get<ChargeBasisStats>(`${this.apiUrl}/stats`);
  }

  addCharge(charge: Omit<ChargeBasis, 'id' | 'createdAt'>): void {
    this.http.post<ChargeBasis>(this.apiUrl, charge).subscribe({
      next: () => this.loadCharges(),
      error: (error) => console.error('Error adding charge basis:', error)
    });
  }

  updateCharge(id: number, charge: Partial<ChargeBasis>): void {
    this.http.put<ChargeBasis>(`${this.apiUrl}/${id}`, charge).subscribe({
      next: () => this.loadCharges(),
      error: (error) => console.error('Error updating charge basis:', error)
    });
  }

  deleteCharge(id: number): void {
    this.http.delete(`${this.apiUrl}/${id}`).subscribe({
      next: () => this.loadCharges(),
      error: (error) => console.error('Error deleting charge basis:', error)
    });
  }

  private getMockCharges(): ChargeBasis[] {
    return [
      {
        id: 1,
        internalName: 'at_cost',
        displayName: 'At Cost',
        appliesToAir: true,
        appliesToSeaLcl: true,
        appliesToSeaFcl: true,
        notes: 'Carrier/local charges passed through',
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 2,
        internalName: 'fixed',
        displayName: 'Fixed Amount',
        appliesToAir: true,
        appliesToSeaLcl: true,
        appliesToSeaFcl: true,
        notes: 'Flat processing/admin charges',
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 3,
        internalName: 'per_awb',
        displayName: 'Per AWB',
        appliesToAir: true,
        appliesToSeaLcl: false,
        appliesToSeaFcl: false,
        notes: 'Per air waybill',
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 4,
        internalName: 'per_bl',
        displayName: 'Per BL (Bill of Lading)',
        appliesToAir: true,
        appliesToSeaLcl: true,
        appliesToSeaFcl: true,
        notes: 'BL fee, manifest, TOF',
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 5,
        internalName: 'per_cbm',
        displayName: 'Per CBM',
        appliesToAir: false,
        appliesToSeaLcl: true,
        appliesToSeaFcl: true,
        notes: 'CFS, ocean freight (LCL)',
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 6,
        internalName: 'per_cntr',
        displayName: 'Per Container',
        appliesToAir: false,
        appliesToSeaLcl: false,
        appliesToSeaFcl: true,
        notes: 'Carrier charges, deposit',
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 7,
        internalName: 'per_cntr_type',
        displayName: 'Per Container Type',
        appliesToAir: false,
        appliesToSeaLcl: false,
        appliesToSeaFcl: true,
        notes: '20GP / 40HQ price split',
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 8,
        internalName: 'per_day',
        displayName: 'Per Day',
        appliesToAir: true,
        appliesToSeaLcl: true,
        appliesToSeaFcl: true,
        notes: 'Storage (ECCF, port, warehouse)',
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 9,
        internalName: 'per_kg',
        displayName: 'Per KG',
        appliesToAir: true,
        appliesToSeaLcl: false,
        appliesToSeaFcl: false,
        notes: 'Air freight, X-ray, etc.',
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 10,
        internalName: 'per_rider',
        displayName: 'Per Rider',
        appliesToAir: true,
        appliesToSeaLcl: true,
        appliesToSeaFcl: true,
        notes: 'Delivery with staff escort',
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 11,
        internalName: 'per_sheet',
        displayName: 'Per Sheet',
        appliesToAir: true,
        appliesToSeaLcl: true,
        appliesToSeaFcl: true,
        notes: 'Certificate of Origin, Notary, etc.',
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 12,
        internalName: 'per_shipment',
        displayName: 'Per Shipment',
        appliesToAir: true,
        appliesToSeaLcl: true,
        appliesToSeaFcl: true,
        notes: 'Docs, export fees, processing',
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 13,
        internalName: 'per_trip',
        displayName: 'Per Trip',
        appliesToAir: true,
        appliesToSeaLcl: true,
        appliesToSeaFcl: true,
        notes: 'Trucking, rider fee',
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 14,
        internalName: 'percentage',
        displayName: 'Percentage (%)',
        appliesToAir: true,
        appliesToSeaLcl: true,
        appliesToSeaFcl: true,
        notes: 'Collect & currency fee, CAF, etc.',
        isActive: true,
        createdAt: new Date()
      }
    ];
  }
}
